import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { documentApi, documentKeys, type FieldInput } from "@/features/documents/api";
import type { QueryParams } from "@/lib/api/client";
import { dashboardKeys } from "@/features/dashboard/hooks";
import { profileKeys } from "@/features/profile/api";

export function useDocuments(filters: QueryParams = {}) {
  return useQuery({
    queryKey: documentKeys.list(filters),
    queryFn: () => documentApi.list(filters),
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.detail(id ?? ""),
    queryFn: () => documentApi.detail(id!),
    enabled: Boolean(id),
  });
}

export function useDocumentEvents(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.events(id ?? ""),
    queryFn: () => documentApi.events(id!),
    enabled: Boolean(id),
  });
}

export function useMyDocuments() {
  return useQuery({ queryKey: documentKeys.mine(), queryFn: documentApi.mine });
}

export function useMyDocument(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.mineDetail(id ?? ""),
    queryFn: () => documentApi.mineDetail(id!),
    enabled: Boolean(id),
  });
}

export function useGroups() {
  return useQuery({ queryKey: documentKeys.groups(), queryFn: documentApi.groups });
}

export function useGroupMembers(id: string | null | undefined) {
  return useQuery({
    queryKey: [...documentKeys.groups(), "members", id ?? ""],
    queryFn: () => documentApi.groupMembers(id!),
    enabled: Boolean(id),
  });
}

function useDocumentMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
      void queryClient.invalidateQueries({ queryKey: documentKeys.groups() });
      // The rail counts documents waiting to be signed, for HR and for staff.
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useCreateDocument() {
  return useDocumentMutation(
    ({
      file,
      title,
      description,
      dueDate,
    }: {
      file: File;
      title: string;
      description?: string;
      dueDate?: string;
    }) => documentApi.create(file, title, description, dueDate),
  );
}

export function useSetSigners(documentId: string) {
  return useDocumentMutation(
    ({ userIds, groupIds }: { userIds: string[]; groupIds?: string[] }) =>
      documentApi.setSigners(documentId, userIds, groupIds ?? []),
  );
}

export function useSetFields(documentId: string) {
  return useDocumentMutation((fields: FieldInput[]) =>
    documentApi.setFields(documentId, fields),
  );
}

export function useSendDocument(documentId: string) {
  return useDocumentMutation(() => documentApi.send(documentId));
}

export function useRemindSigners(documentId: string) {
  return useDocumentMutation(() => documentApi.remind(documentId));
}

export function useCancelDocument(documentId: string) {
  return useDocumentMutation((reason?: string) => documentApi.cancel(documentId, reason));
}

export function useSignDocument(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      mode: "drawn" | "profile";
      data_url?: string;
      save_to_profile?: boolean;
    }) => documentApi.sign(documentId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      // Signing can save the signature to the profile, and the profile is
      // where every other screen reads it from.
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useDeclineDocument(documentId: string) {
  return useDocumentMutation((reason: string) => documentApi.decline(documentId, reason));
}

export function useCreateGroup() {
  return useDocumentMutation(({ name, description }: { name: string; description?: string }) =>
    documentApi.createGroup(name, description),
  );
}

export function useSetGroupMembers() {
  return useDocumentMutation(({ id, userIds }: { id: string; userIds: string[] }) =>
    documentApi.setGroupMembers(id, userIds),
  );
}

export function useDeleteGroup() {
  return useDocumentMutation((id: string) => documentApi.deleteGroup(id));
}
